// Owner stamp confirmation overlay. Visual chrome is shared with the
// staff overlay (see ../../shared/stamp/StampOverlayChrome).
//
// Slice 4b iteration — flow is wait-for-server. No optimistic close: the
// slide locks-right + shows a spinner while the +1 (or redemption) is
// in flight; when the server confirms we transition to the next state
// in a single paint (close + toast, OR show picker with fresh data),
// eliminating the flashes that came from rendering cached/seed values
// between optimistic close and refetch.

import { useEffect, useRef, useState } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { newId } from '@naya/shared'

import { ownerApi, OwnerApiError } from '../ownerApi.js'
import { usePrograms } from '../program/hooks/useProgram.js'
import { playStampSuccessChime } from '../../shared/sound/stampSound.js'
import {
  RedemptionConfirmModal,
  type Milestone as RedeemableMilestone,
  type SecretMenuContext,
} from '../../shared/redemption/RedemptionUI.js'
import {
  RewardsPickerModal,
  stampToastMessage,
  StampOverlayBlocked,
  StampOverlayBody,
  StampOverlayShell,
  StampOverlaySkeleton,
  type StampOverlayReferralContext,
} from '../../shared/stamp/StampOverlayChrome.js'
import { errorMessageFor } from '../errorMessages.js'

export interface StampCard {
  customer_id: string
  first_name: string | null
  card_id: string
  card_code: string
  stamps_count: number
  total_stamps: number
  /** 'completed' only under completion_behavior 'stay_completed'. The
   *  overlay opens such a card in its "card complete" state — the owner
   *  can still view it (and reset it, see Part C). */
  status: 'active' | 'completed'
  /** Search results only — the card's program lets staff recover its pass. */
  staff_can_recover_pass?: boolean
}

interface IssueStampResponse {
  card: {
    id: string
    stamps_count: number
    tenant_id: string
  }
  issuance: { id: string; stamp_number: number }
  newly_earned_milestones: Array<{
    at_stamp: number
    reward_text: string
    type: 'intermediate' | 'final'
  }>
  requested_count: number
  stamps_added: number
  secret_stamps_added: number
}

interface CardDetail {
  card: {
    id: string
    stamps_count: number
    status: 'active' | 'completed'
    program_snapshot: { total_stamps: number; name?: string }
  }
  // Customer lifecycle (2026-09-05) — deactivated_at/erased_at drive the
  // first-paint revoked/erased block below (see StampOverlayBlocked).
  customer?: {
    deactivated_at: string | null
    deactivation_reason: string | null
    erased_at: string | null
  } | null
  pending_milestones: RedeemableMilestone[]
  referral_context?: StampOverlayReferralContext | null
  // Secret menu — see SecretMenuContext doc comment. Null unless this card
  // has a pending FINAL milestone right now.
  secret_menu_context?: SecretMenuContext | null
  // Whether off-menu tagging is live for this card's tenant/program (env +
  // entitlement + owner toggle). Gates the off-menu stepper.
  secret_menu_adjustable?: { enabled: boolean }
}

export function StampConfirmOverlay({
  card,
  branchId,
  onDone,
}: {
  card: StampCard
  branchId: string | null
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  // Multi-stamp selector — how many stamps the next slide adds. Reset to 1
  // after every successful stamp so a leftover count never silently carries
  // over to the next customer.
  const [count, setCount] = useState(1)
  // Secret menu — how many of `count` stamps in this action are off-menu.
  // Clamped to never exceed `count`; reset to 0 alongside `count` after
  // every successful stamp (see stampMutation.onSuccess below).
  const [secretCount, setSecretCount] = useState(0)
  useEffect(() => {
    setSecretCount((s) => Math.min(s, count))
  }, [count])
  const [earned, setEarned] = useState<RedeemableMilestone[]>([])
  const [redeemTarget, setRedeemTarget] = useState<RedeemableMilestone | null>(
    null,
  )
  // True when `earned` was populated by a stamp mutation we just ran (not
  // by the pre-stamp first-paint auto-open). Controls what "Skip for now"
  // does: post-stamp skip ends the session; pre-stamp skip returns to
  // the slide so this visit's stamp can still be added.
  const earnedFromStampRef = useRef(false)
  // Multi-stamp selector — the just-completed mutation's requested/added
  // counts, so the "Skip for now" toast (fired after the picker, once the
  // mutation's own onSuccess closure has already exited) can still report
  // an accurate "Added 4 of 5 stamps" message.
  const lastStampResultRef = useRef({
    requested_count: 1,
    stamps_added: 1,
    secret_stamps_added: 0,
  })
  // True once any reward has been redeemed in this overlay session. After
  // a redeem, the overlay nudges toward Done — the slide stays available
  // for the combined buy+redeem case but loses primary emphasis.
  const [redeemedThisSession, setRedeemedThisSession] = useState(false)

  const customerName = card.first_name?.trim() || 'Customer'
  const detailKey = ['owner', 'card-detail', card.card_id] as const

  // Show the program name (pill above the customer name) only when the tenant
  // runs more than one active loyalty program — otherwise it's redundant.
  const programsQ = usePrograms()
  const multiProgram =
    (programsQ.data?.programs ?? []).filter(
      (p) => p.status === 'active' && p.pass_type === 'loyalty',
    ).length > 1

  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () =>
      ownerApi.get<CardDetail>(`/cards/${encodeURIComponent(card.card_id)}`),
  })
  const data = detail.data

  const pickerDismissedRef = useRef(false)

  // Stamp mutation — WAITS for the server. Slide goes pending → server
  // responds → either show picker (milestone earned) or close+toast.
  // Cache is updated from the response (setQueryData); the invalidate is
  // fire-and-forget so the response → next-UI-state transition lands in
  // the same paint as the network response.
  const idempotencyKeyRef = useRef<string>(newId())
  const stampMutation = useMutation({
    mutationFn: () => {
      if (!branchId) throw new Error('Branch not selected')
      const body = {
        branch_id: branchId,
        idempotency_key: idempotencyKeyRef.current,
        count,
        secret_count: secretCount,
      }
      return ownerApi.post<IssueStampResponse>(
        `/cards/${encodeURIComponent(card.card_id)}/stamps`,
        body,
      )
    },
    onSuccess: (result) => {
      playStampSuccessChime()
      idempotencyKeyRef.current = newId()
      setCount(1)
      setSecretCount(0)
      lastStampResultRef.current = {
        requested_count: result.requested_count,
        stamps_added: result.stamps_added,
        secret_stamps_added: result.secret_stamps_added,
      }
      queryClient.setQueryData<CardDetail>(detailKey, (prev) =>
        prev
          ? {
              ...prev,
              card: { ...prev.card, stamps_count: result.card.stamps_count },
              pending_milestones: mergePending(
                prev.pending_milestones,
                result.newly_earned_milestones,
              ),
            }
          : prev,
      )
      void queryClient.invalidateQueries({ queryKey: detailKey })
      void queryClient.invalidateQueries({ queryKey: ['owner', 'customers'] })

      const finalCount = result.card.stamps_count
      const totalStamps =
        data?.card.program_snapshot.total_stamps ?? card.total_stamps

      if (result.newly_earned_milestones.length > 0) {
        pickerDismissedRef.current = false
        earnedFromStampRef.current = true
        const cached = queryClient.getQueryData<CardDetail>(detailKey)
        const allPending = mergePending(
          cached?.pending_milestones ?? [],
          result.newly_earned_milestones,
        )
        setEarned(allPending)
      } else {
        toast.success(
          stampToastMessage(customerName, finalCount, totalStamps, result),
        )
        onDone()
      }
    },
    onError: (err) => {
      const message =
        err instanceof OwnerApiError && err.code
          ? errorMessageFor(err.code)
          : err instanceof OwnerApiError
            ? err.message
            : 'Stamp failed'
      toast.error(message)
    },
  })

  function fireStamp() {
    if (!branchId) {
      toast.error('Branch not selected')
      return
    }
    if (!data || stampMutation.isPending) return
    stampMutation.mutate()
  }

  // See StaffStampOverlay for the rationale — render-time check replaces
  // the deleted useEffect so a background refetch can't flicker the
  // picker closed-then-open.
  const showPickerFromData =
    !!data &&
    data.pending_milestones.length > 0 &&
    earned.length === 0 &&
    !redeemTarget &&
    !pickerDismissedRef.current &&
    !stampMutation.isPending
  const pickerMilestones = earned.length > 0
    ? earned
    : showPickerFromData
      ? data!.pending_milestones
      : []

  // Customer lifecycle (2026-09-05) — block before the slide ever renders,
  // rather than letting staff/owner slide into a generic 423 toast.
  const blockedKind: 'erased' | 'revoked' | null = data?.customer?.erased_at
    ? 'erased'
    : data?.customer?.deactivated_at
      ? 'revoked'
      : null

  return (
    <StampOverlayShell>
      {!data ? (
        <StampOverlaySkeleton onCancel={onDone} />
      ) : blockedKind ? (
        <StampOverlayBlocked
          customerName={customerName}
          kind={blockedKind}
          at={
            blockedKind === 'erased'
              ? data.customer!.erased_at
              : data.customer!.deactivated_at
          }
          reason={data.customer?.deactivation_reason}
          onCancel={onDone}
        />
      ) : (
        <StampOverlayBody
          data={data}
          customerName={customerName}
          cardCode={card.card_code}
          programName={
            multiProgram ? data.card.program_snapshot.name : undefined
          }
          referral={data.referral_context ?? null}
          onCancel={onDone}
          onSlideConfirm={fireStamp}
          onClaimReward={() => setEarned(data.pending_milestones)}
          slideDisabled={!branchId}
          slidePending={stampMutation.isPending}
          pickerOpen={pickerMilestones.length > 0 || redeemTarget !== null}
          postRedeem={redeemedThisSession}
          count={count}
          onCountChange={setCount}
          secretCount={secretCount}
          onSecretCountChange={
            data.secret_menu_adjustable?.enabled ? setSecretCount : undefined
          }
        />
      )}

      {pickerMilestones.length > 0 && !redeemTarget && data && (
        <RewardsPickerModal
          name={customerName}
          milestones={pickerMilestones}
          onRedeem={(m) => setRedeemTarget(m)}
          secretMenuContext={data.secret_menu_context ?? null}
          onSkip={() => {
            if (earnedFromStampRef.current) {
              earnedFromStampRef.current = false
              setEarned([])
              const finalCount = data?.card.stamps_count ?? card.stamps_count
              const totalStamps =
                data?.card.program_snapshot.total_stamps ?? card.total_stamps
              toast.success(
                stampToastMessage(
                  customerName,
                  finalCount,
                  totalStamps,
                  lastStampResultRef.current,
                ),
              )
              onDone()
              return
            }
            pickerDismissedRef.current = true
            setEarned([])
          }}
        />
      )}

      {redeemTarget && (
        <RedemptionConfirmModal
          milestone={redeemTarget}
          cardId={card.card_id}
          branchId={branchId}
          customerName={card.first_name ?? undefined}
          secretMenuContext={data?.secret_menu_context ?? null}
          postRedemption={(cardId, body) =>
            ownerApi.post(
              `/cards/${encodeURIComponent(cardId)}/redemptions`,
              body,
            )
          }
          errorMessageFor={errorMessageFor}
          onClose={() => setRedeemTarget(null)}
          onRedeemed={(m) => {
            setEarned((prev) => prev.filter((x) => x.at_stamp !== m.at_stamp))
            queryClient.setQueryData<CardDetail>(detailKey, (prev) =>
              prev
                ? {
                    ...prev,
                    pending_milestones: prev.pending_milestones.filter(
                      (x) => x.at_stamp !== m.at_stamp,
                    ),
                  }
                : prev,
            )
            setRedeemedThisSession(true)
          }}
          onCommitted={() => {
            void queryClient.invalidateQueries({ queryKey: detailKey })
          }}
        />
      )}
    </StampOverlayShell>
  )
}

/** Merge two milestone lists keyed by at_stamp, preferring the second
 *  list's entry on collision (fresher reward text in case the program
 *  text was tweaked between stamps). */
function mergePending(
  a: RedeemableMilestone[],
  b: RedeemableMilestone[],
): RedeemableMilestone[] {
  const map = new Map<number, RedeemableMilestone>()
  for (const m of a) map.set(m.at_stamp, m)
  for (const m of b) map.set(m.at_stamp, m)
  return Array.from(map.values()).sort((x, y) => x.at_stamp - y.at_stamp)
}
